import { EditTodoIcon } from '@/assets/icons/info-icons/editTodo';
import { AddButtonIcon } from '@/assets/static/icons/add-button-icon';
import { DeleteItemIcon } from '@/assets/static/icons/delete-item';
import Button from '@/components/basicComponents/button';
import { FormFooter } from '@/components/basicComponents/formFooter';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import styled, { useTheme } from 'styled-components';
import { FormFieldsWrapper } from '..';
import { EnterVehicleInfoModalPage } from '../enterVehicleInfoModalPage';
import DeleteVehicle from '../modals/deleteVehicle';
import { InvestmentVehicleDto } from '../types';

interface IForm {
  investmentVehicles: InvestmentVehicleDto[];
}

const defaultFormValues: IForm = {
  investmentVehicles: []
};

interface VehiclesModalPageProps {
  title: string;
  investmentId: string;
  investmentVehicles: InvestmentVehicleDto[];
  investmentVehiclesRefetch: () => void;
  handleCloseEditModalPage: () => void;
}

export const VehiclesModalPage = ({
  title,
  investmentId,
  investmentVehicles,
  investmentVehiclesRefetch,
  handleCloseEditModalPage
}: VehiclesModalPageProps) => {
  const { isMobile } = useResponsive();
  const theme = useTheme();
  const { control, reset } = useForm<IForm>({ defaultValues: defaultFormValues });
  const { fields } = useFieldArray({
    control,
    name: 'investmentVehicles',
    keyName: 'fieldId'
  });

  const [showZeroState, setShowZeroState] = useState<boolean>(false);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [isVehicleInfoModalPageOpen, setIsVehicleInfoModalPageOpen] = useState<boolean>(false);
  const [currentVehicle, setCurrentVehicle] = useState<InvestmentVehicleDto | null>(null);

  useEffect(() => {
    if (!investmentVehicles.length) {
      setShowZeroState(true);
      return;
    }
    setShowZeroState(false);
    reset((defaultFormData) => ({ ...defaultFormData, investmentVehicles }));
  }, [investmentVehicles]);

  const handleModalWindow = ({ open, type, field }: { open: boolean; type: string; field?: InvestmentVehicleDto }) => {
    setModalWindow({ ...modalWindow, isOpen: open, type });
    setCurrentVehicle(field ?? null);
  };

  const handleVehicleInfoModalPage = ({ open, field }: { open: boolean; field?: InvestmentVehicleDto }) => {
    setIsVehicleInfoModalPageOpen(open);
    setCurrentVehicle(field ?? null);
  };

  if (isVehicleInfoModalPageOpen) {
    return (
      <EnterVehicleInfoModalPage
        investmentId={investmentId}
        currentVehicle={currentVehicle}
        investmentVehiclesRefetch={investmentVehiclesRefetch}
        handleCloseModalPage={() => handleVehicleInfoModalPage({ open: false })}
      />
    );
  }

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'delete-vehicle' && (
        <DeleteVehicle
          isOpen={modalWindow.isOpen}
          currentVehicle={currentVehicle}
          investmentVehiclesRefetch={investmentVehiclesRefetch}
          onClose={() => handleModalWindow({ open: false, type: '' })}
        />
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleCloseEditModalPage} backToTitle={''} />} />
        <PageTitle title={title ?? ''} />
        <PaddingWrap>
          <Form id="vehiclesForm">
            <FormFieldsWrapper>
              {showZeroState ? (
                <SectionZeroState>
                  <SectionZeroStateText>No vehicles have been added yet.</SectionZeroStateText>
                  <Button
                    onClick={() => handleVehicleInfoModalPage({ open: true })}
                    styleType="outline"
                    icon={<AddButtonIcon fill={theme.font.action} />}
                    size="md"
                    type="button"
                    width="max-content"
                  >
                    Add
                  </Button>
                </SectionZeroState>
              ) : (
                <VehiclesWrapper>
                  {fields.map((item) => (
                    <SectionContent key={item.fieldId} isMobile={isMobile}>
                      <SectionData>
                        <SectionDataItemWrapper isMobile={isMobile}>
                          <SectionDataItem isBold>Vehicle Code</SectionDataItem>
                          <SectionDataItem>{item.id}</SectionDataItem>
                        </SectionDataItemWrapper>
                        {item.name && (
                          <SectionDataItemWrapper isMobile={isMobile}>
                            <SectionDataItem isBold>Name</SectionDataItem>
                            <SectionDataItem>{item.name}</SectionDataItem>
                          </SectionDataItemWrapper>
                        )}
                        {typeof item.taxExempt === 'boolean' && (
                          <SectionDataItemWrapper isMobile={isMobile}>
                            <SectionDataItem isBold>Tax Status</SectionDataItem>
                            <SectionDataItem>{item.taxExempt ? 'Exempt' : 'Taxable'}</SectionDataItem>
                          </SectionDataItemWrapper>
                        )}
                      </SectionData>
                      <ButtonsWrapper id="editButton" isMobile={isMobile}>
                        <DeleteIconWrapper isMobile={isMobile} onClick={() => handleModalWindow({ open: true, type: 'delete-vehicle', field: item })}>
                          <DeleteItemIcon width={23} height={23} fill={theme.context.error} />
                        </DeleteIconWrapper>
                        <EditIconWrapper isMobile={isMobile} onClick={() => handleVehicleInfoModalPage({ open: true, field: item })}>
                          <EditTodoIcon />
                        </EditIconWrapper>
                      </ButtonsWrapper>
                    </SectionContent>
                  ))}
                  <Divider />
                  <Button
                    onClick={() => handleVehicleInfoModalPage({ open: true })}
                    styleType="outline"
                    icon={<AddButtonIcon fill={theme.font.action} />}
                    size="md"
                    width="max-content"
                    type="button"
                  >
                    Add
                  </Button>
                </VehiclesWrapper>
              )}
            </FormFieldsWrapper>
            <FormFooter
              onCancel={handleCloseEditModalPage}
              onSave={handleCloseEditModalPage}
              disableSaveButton={false}
              showRequiredFieldsWarning={false}
            />
          </Form>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

const Form = styled.form`
  padding: 50px 0;
  width: 100%;
  background: ${({ theme }) => theme.layer.base};
`;

const SectionZeroState = styled.div`
  display: flex;
  gap: 10px;
  flex-direction: column;
  padding: 0 10px;
`;

const SectionZeroStateText = styled.div`
  padding: 10px 0;
  font-size: 19px;
  font-weight: 400;
  line-height: 26.6px;
  color: ${({ theme }) => theme.font.weak};
`;

const VehiclesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionContent = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 15px;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  padding: ${({ isMobile }) => (isMobile ? '0' : '20px')};

  @media (hover: hover) {
    &:hover #editButton {
      opacity: 1;
    }

    &:hover {
      background-color: ${({ theme }) => theme.layer[12]};
    }
  }
`;

const SectionData = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
`;

const SectionDataItemWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 20px;
  width: ${({ isMobile }) => (isMobile ? '100%' : '60%')};
  padding: 2px 10px;
`;

const SectionDataItem = styled.div<{ isBold?: boolean }>`
  font-family: Blinker;
  font-size: 16px;
  font-weight: ${({ isBold }) => (isBold ? 600 : 400)};
  line-height: 22.4px;
  color: ${({ theme }) => theme.font.base};
  padding: 4px;
`;

const ButtonsWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  width: ${({ isMobile }) => (isMobile ? '100%' : 'max-content')};
  gap: 10px;
  margin-left: ${({ isMobile }) => (isMobile ? '0' : 'auto')};
  opacity: ${({ isMobile }) => (isMobile ? '1' : '0')};
  transition: 0.1s ease-in-out;
`;

const EditIconWrapper = styled.div<{ isMobile: boolean }>`
  width: ${({ isMobile }) => (isMobile ? '100%' : 'max-content')};
  padding: 12px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.action.default};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: auto;
  cursor: pointer;
`;

const DeleteIconWrapper = styled.div<{ isMobile: boolean }>`
  width: ${({ isMobile }) => (isMobile ? '100%' : '40px')};
  height: 40px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.context.error};
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  margin: 20px 0;
  background-color: ${({ theme }) => theme.border.base};
`;
